/**
 * The `team_type` enum and how the Teams screens present it.
 *
 * Commission rules keep their own narrower list (`commissionManagerLinks.TEAM_TYPES`): EPC teams
 * are partner companies, not a basis for commission overrides.
 */
export const ALL_TEAM_TYPES = ['SALES', 'INTERNAL_PM', 'INSTALLER', 'BIZ_DEV', 'EPC'] as const;
export type TeamTypeValue = (typeof ALL_TEAM_TYPES)[number];

export const TEAM_TYPE_LABEL: Record<TeamTypeValue, string> = {
  SALES: 'Sales',
  INTERNAL_PM: 'Internal PM',
  INSTALLER: 'Installer',
  BIZ_DEV: 'Business Development',
  EPC: 'EPC',
};

export function teamTypeLabel(type: string): string {
  return TEAM_TYPE_LABEL[type as TeamTypeValue] ?? type.replace(/_/g, ' ');
}

/**
 * Types the Teams tab can create. EPC and Installer are outside companies: they are partner
 * companies (installer_partners) on the Partners tab, not teams.
 */
const CREATABLE: TeamTypeValue[] = ['SALES', 'INTERNAL_PM', 'BIZ_DEV'];

/**
 * Options for the team form's type picker. An edited team whose type is not creatable here keeps
 * that type as an option, so saving the form never silently retypes it.
 */
export function creatableTeamTypeOptions(current?: string): { label: string; value: string }[] {
  const values: string[] = [...CREATABLE];
  if (current && !values.includes(current)) values.push(current);
  return values.map((value) => ({ label: teamTypeLabel(value), value }));
}

export interface TeamTypeChip {
  value: string;
  label: string;
  count: number;
}

/** Filter chips for the Teams tab: All, every creatable type, then any other type a team uses. */
export function teamTypeFilterChips(teams: { type: string }[]): TeamTypeChip[] {
  const counts = new Map<string, number>();
  for (const t of teams) counts.set(t.type, (counts.get(t.type) ?? 0) + 1);
  const values: string[] = [...CREATABLE];
  for (const type of counts.keys()) if (!values.includes(type)) values.push(type);
  return [
    { value: 'ALL', label: 'All', count: teams.length },
    ...values.map((value) => ({ value, label: teamTypeLabel(value), count: counts.get(value) ?? 0 })),
  ];
}

/**
 * Sub-tabs of Settings > Users. 'internal-teams' merged into 'teams' and 'installers' became
 * 'partners'; old links still land.
 */
const USERS_SUBTABS = ['users', 'teams', 'partners'];
export function normaliseTeamsSubtab(value: string | null): string {
  if (value === 'internal-teams') return 'teams';
  if (value === 'installers') return 'partners';
  return value && USERS_SUBTABS.includes(value) ? value : 'users';
}

/** Badge colours per type; the label always accompanies the colour. */
export const TEAM_TYPE_BADGE_CLASS: Record<string, string> = {
  SALES: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  EPC: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  INTERNAL_PM: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  BIZ_DEV: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  INSTALLER: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
};
